// /* eslint-disable @typescript-eslint/no-explicit-any */
import { addToast, Card, CardBody, CardHeader, Image } from '@heroui/react';
import { SlLocationPin } from 'react-icons/sl';
import { FaRegBookmark, FaStar, FaHeart, FaRegHeart } from 'react-icons/fa6';
import { TbRosetteDiscountCheckFilled } from 'react-icons/tb';
import { all_routes } from '../../../../core/data/routes/all_routes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { track } from '../../../../lib/telemetry';
import { resolveBookingEntrySource } from '../../../../lib/telemetryUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  // PackageGet,
  ServiceCardType,
  ServiceGet,
} from '../../../../utils/type';
import CustomButton from '../../CustomButton';
import { useAuth } from '../../../../context/AuthContext';
import { useEffect, useRef, useState } from 'react';
import MessageModal from './MessageModal';
import fallbackImage from '../../../../../public/fallback-image.png';
import CommonConfirmModal from './CommonConfirmModal';
import SponsoredBadge from '../../sponsorship/SponsoredBadge';

import {
  addFavoriteService,
  getAvailableFavoriteServiceIds,
  getServiceFavoriteCountPublic,
  removeFavoriteService,
} from '../../../../service/addFavService';
import { formatCurrency } from '../../../../utils/currency';
import { formatCompactNumber } from '../../../../utils/numberFormat';
import { getBusinessProfilePath } from '../../../../utils/businessProfile';
import { trackSponsorshipEvent } from '../../../../api/service/sponsorshipApi';
import { ERROR_CODE } from '../../../../utils/enum';
import {
  getServiceMinTierPrice,
  resolveBookingRoute,
} from '../../../../utils/common';

// Session-level dedupe set — prevents firing the same impression twice
// within one browser session.
const firedImpressions = new Set<string>();

interface ServiceCardProps {
  data: ServiceCardType;
  user?: any;
  initialFavorite?: boolean;
  mode?: 'default' | 'favoriteDashboard';
  onRemoveFavorite?: (serviceId: string) => void;
  isRemoveLoading?: boolean;
  ratingValueOverride?: number;
  ratingCountOverride?: number;
  // Only set when this card came from an AI-ranked result set (Prowl "Ask
  // Prowl" search / conciergeSearch). Absent/undefined for regular browsing —
  // there's no real match score to show without a query to match against.
  matchPct?: number | null;
  // Sponsorship props — when true, the card gets a cyan ring and Sponsored badge
  isSponsored?: boolean;
  onSponsoredClick?: () => void;
  // Sponsorship document _id for impression/click tracking.
  // When set, the card fires impression on viewport entry (once per session)
  // and click on "Book Now".
  sponsorshipId?: string;
  // Quote broadcast multi-select props
  isBroadcastSelectable?: boolean;
  isSelected?: boolean;
  onSelectChange?: (
    serviceId: string,
    providerId: string,
    businessId: string,
    isSelected: boolean
  ) => void;
  isSelectionDisabled?: boolean;
}

const ServiceCard = ({
  data,
  initialFavorite = false,
  mode = 'default',
  onRemoveFavorite,
  isRemoveLoading = false,
  ratingValueOverride,
  ratingCountOverride,
  matchPct,
  isSponsored = false,
  onSponsoredClick,
  sponsorshipId,
  isBroadcastSelectable = false,
  isSelected = false,
  onSelectChange,
  isSelectionDisabled = false,
}: ServiceCardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { userId, isAuthenticated, customerAccountStatus } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const routes = all_routes;
  const cardRef = useRef<HTMLDivElement>(null);

  // ── Impression tracking (sponsored cards only) ──────────────────────
  // Fires once per sponsorship ID per browser session when the card
  // scrolls into the viewport. Uses IntersectionObserver with a 50%
  // visibility threshold.
  useEffect(() => {
    if (!sponsorshipId || firedImpressions.has(sponsorshipId)) return;

    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          firedImpressions.add(sponsorshipId);
          trackSponsorshipEvent(sponsorshipId, 'impression');
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sponsorshipId]);

  // ── Click tracking (sponsored cards only) ───────────────────────────
  const handleBookNowClick = (serviceId: string) => {
    if (sponsorshipId) {
      trackSponsorshipEvent(sponsorshipId, 'click');
    }
    handleNavigate(serviceId);
  };

  const handleNavigate = (id: string) => {
    if (!id) {
      addToast({
        title: 'Service Id is missing',
        description: 'Service Id not found, try again',
        radius: 'md',
        color: 'danger',
      });
      console.error('Service id not found');
      return;
    }
    const providerId = data?.providerId || '';

    if (!isAuthenticated || customerAccountStatus !== 'active') {
      setIsModalOpen(true);
      return;
    }

    if (userId === providerId) {
      addToast({
        title: 'Booking Error',
        description: 'You cannot book your own service',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    // Telemetry: booking.started — user clicked "Book Now" on this card.
    track('booking.started', {
      serviceId: String(id),
      entrySource: resolveBookingEntrySource(location.pathname),
      bookingType: data?.bookingType || 'SLOT',
    });

    // C1: branch the Book-Now CTA by the service's bookingType.
    // SLOT → existing slot flow; QUOTE → Job Request Form; PACKAGE → Package
    // Selection. resolveBookingRoute defaults to SLOT when bookingType is absent.
    navigate(resolveBookingRoute(data?.bookingType).replace(':serviceId', id), {
      state: {
        serviceId: id,
      },
    });
  };

  const handleCardClick = () => {
    if (sponsorshipId) {
      trackSponsorshipEvent(sponsorshipId, 'click');
    }
    const url = `${routes.serviceDetails}/${data.displayId || data.serviceId}/${data.slug}`;
    navigate(url);
  };

  // const getPackageLowestPrice = (packages: PackageGet[] | undefined) => {
  //   if (!packages || packages.length === 0) return undefined;
  //   return packages.reduce((lowest, pkg) => {
  //     return pkg.price < lowest ? pkg.price : lowest;
  //   }, packages[0].price);
  // };

  // const calculateDiscount = (
  //   price?: string,
  //   discount?: string,
  //   type?: 'amount' | 'percentage' | undefined
  // ) => {
  //   if (!price) return 0;
  //   const priceNum = Number(price);
  //   if (!discount || !type) return priceNum;

  //   const discountNum = Number(discount);
  //   return type === 'amount'
  //     ? Math.max(priceNum - discountNum, 0)
  //     : Math.max(priceNum - (priceNum * discountNum) / 100, 0);
  // };

  // const navigateToProfile = (id: string) => {};

  const queryClient = useQueryClient();
  const serviceId = data?.serviceId ? String(data.serviceId) : '';

  const [localFavorited, setLocalFavorited] = useState<boolean>(
    Boolean(initialFavorite)
  );

  const { data: favoriteIds = [] } = useQuery({
    queryKey: ['ADD-FAVORITE-SERVICE-IDS'],
    queryFn: getAvailableFavoriteServiceIds,
    enabled: mode === 'default' && Boolean(isAuthenticated),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: publicFavoriteCount = 0 } = useQuery({
    queryKey: ['ADD-FAVORITE-PUBLIC-COUNT', serviceId],
    queryFn: () => getServiceFavoriteCountPublic(serviceId),
    enabled: mode === 'default' && Boolean(serviceId),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setLocalFavorited(Boolean(initialFavorite));
  }, [initialFavorite]);

  useEffect(() => {
    if (!isAuthenticated || mode !== 'default') return;
    setLocalFavorited(favoriteIds.includes(serviceId));
  }, [favoriteIds, isAuthenticated, mode, serviceId]);

  const addFavMutation = useMutation({
    mutationFn: async () => addFavoriteService(serviceId),
    onSuccess: () => {
      setLocalFavorited(true);
      queryClient.invalidateQueries({ queryKey: ['ADD-FAVORITE-SERVICE-IDS'] });
      queryClient.invalidateQueries({
        queryKey: ['ADD-FAVORITE-PUBLIC-COUNT', serviceId],
      });
      addToast({ title: 'Added to favorites', radius: 'md', color: 'success' });
    },
    onError: (err: any) => {
      addToast({
        title: 'Failed to add favorite',
        description: err?.message || 'Please try again',
        radius: 'md',
        color: 'danger',
      });
    },
  });

  const removeFavMutation = useMutation({
    mutationFn: async () => removeFavoriteService(serviceId),
    onSuccess: () => {
      setLocalFavorited(false);
      queryClient.invalidateQueries({ queryKey: ['ADD-FAVORITE-SERVICE-IDS'] });
      queryClient.invalidateQueries({
        queryKey: ['ADD-FAVORITE-PUBLIC-COUNT', serviceId],
      });
      addToast({
        title: 'Removed from favorites',
        radius: 'md',
        color: 'warning',
      });
    },
    onError: (err: any) => {
      addToast({
        title: 'Failed to remove favorite',
        description: err?.message || 'Please try again',
        radius: 'md',
        color: 'danger',
      });
    },
  });

  const handleToggleFavorite = () => {
    if (mode === 'favoriteDashboard') return;
    if (!isAuthenticated || customerAccountStatus !== 'active') {
      setIsModalOpen(true);
      return;
    }
    if (!serviceId) {
      addToast({
        title: 'Missing service reference',
        description:
          'Service identifier is not available yet. Please try again.',
        radius: 'md',
        color: 'warning',
      });
      return;
    }

    if (localFavorited) {
      removeFavMutation.mutate();
    } else {
      addFavMutation.mutate();
    }
  };

  const typedData = data as unknown as ServiceGet & {
    averageRating?: number;
    totalReviews?: number;
  };

  const displayAverageRating = Number(
    ratingValueOverride ?? typedData.averageRating ?? data.avg_rating ?? 0
  );

  const displayReviewCount = Number(
    ratingCountOverride ?? typedData.totalReviews ?? data.review_count ?? 0
  );

  // Safe location access with fallbacks
  const locationCity = data?.location?.[0]?.city || 'N/A';
  const locationState = data?.location?.[0]?.state || 'N/A';

  // Phase 4: search/category-browse CTA branching. Reuse resolveBookingRoute
  // for navigation and key the visible badge + button label off bookingType.
  const bookingType = (data?.bookingType || 'SLOT').toUpperCase();
  const isQuoteBooking = bookingType === 'QUOTE';
  const isPackageBooking = bookingType === 'PACKAGE';
  const bookingCtaLabel = isQuoteBooking
    ? 'Request a Quote'
    : isPackageBooking
      ? 'View Packages'
      : 'Book Now';

  console.log('ServiceCard data:', data);

  console.log('ServiceCard typedData:', data);
  const handleSelectToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isBroadcastSelectable || isSelectionDisabled || !onSelectChange)
      return;
    const providerId = data?.providerId || '';
    const businessId = data?.businessId || '';
    const serviceId = data?.serviceId ? String(data.serviceId) : '';
    onSelectChange(serviceId, providerId, businessId, !isSelected);
  };

  return (
    <>
      <div
        ref={cardRef}
        className={`group cursor-pointer border border-gray-200 transition-all duration-300 ease-out w-full rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${isSponsored ? 'ring-2 ring-[#0CA7C1]/40' : ''} ${isBroadcastSelectable && isSelected ? 'ring-2 ring-brandOcean/50' : ''}`}
        onClick={handleCardClick}
      >
        <div className="relative">
          <div className="bg-gray-200 flex justify-center items-center cursor-pointer w-full h-[200px] relative overflow-hidden">
            <Link
              to={`${routes.serviceDetails}/${data.displayId || data.serviceId}/${data.slug}`}
              onClick={(e) => {
                e.stopPropagation();
                if (sponsorshipId)
                  trackSponsorshipEvent(sponsorshipId, 'click');
              }}
            >
              <Image
                src={data?.gallery?.[0]?.serviceImages?.[0] || fallbackImage}
                alt={data?.serviceTitle}
                className="object-cover w-full h-full transition-transform duration-300 ease-out group-hover:scale-105"
                shadow="none"
                isZoomed
                radius="none"
              />
            </Link>
          </div>

          <div className="absolute top-2 left-2 z-20 flex flex-col items-start gap-1.5">
            {isSponsored && (
              <SponsoredBadge
                onClick={(e) => {
                  e.stopPropagation();
                  onSponsoredClick?.();
                }}
                className="cursor-pointer"
                badgeTier={
                  data?.badgeTier ?? (data?.pinned ? 'elite' : 'basic')
                }
              />
            )}
            <small className="bg-slate-900/70 text-white backdrop-blur-sm px-2.5 py-1 rounded-full text-[11px] font-medium">
              {data.categoryName ||
                data?.category?.categoryName ||
                'Unknown Category'}
            </small>

            {isQuoteBooking && (
              <small className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                Request a Quote
              </small>
            )}

            {isPackageBooking && (
              <small className="bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                View Packages
              </small>
            )}

            {typeof matchPct === 'number' && (
              <small className="bg-brandOcean text-white px-2.5 py-[3px] rounded-full text-[10.5px] font-bold">
                {matchPct}% match
              </small>
            )}

            {data?.isPackage && data?.packages?.length
              ? data.packages.some((p) => p.isDiscount) && (
                  <small className="flex items-center text-[10px] sm:text-[11px] bg-green-300 text-gray-700 px-2 py-[2px] rounded-full font-semibold">
                    <TbRosetteDiscountCheckFilled className="mr-1 text-xs text-green-700" />
                    Discount Available
                  </small>
                )
              : data?.isDiscount && (
                  <small className="flex items-center text-body-bold bg-green-300 text-gray-700 px-2 py-[2px] rounded-full">
                    <TbRosetteDiscountCheckFilled className="mr-1 text-body text-green-700" />
                    {`${data?.discount?.valueType !== 'percentage' ? '$' : ''}${data?.discount?.amount}${data?.discount?.valueType === 'percentage' ? '%' : ''} Save`}
                  </small>
                )}
          </div>

          <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5">
            {mode === 'default' && customerAccountStatus !== 'deactivated' && (
              <button
                aria-label="Toggle favorite"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFavorite();
                }}
                className="rounded-full bg-white/90 p-2 shadow-sm hover:scale-105 transition"
              >
                {localFavorited ? (
                  <FaHeart className="text-red-500" />
                ) : (
                  <FaRegHeart className="text-gray-700" />
                )}
              </button>
            )}

            {isBroadcastSelectable && onSelectChange && (
              <button
                aria-label={
                  isSelected ? 'Deselect provider' : 'Select provider'
                }
                onClick={handleSelectToggle}
                disabled={isSelectionDisabled}
                className={`rounded-full bg-white/90 p-2 shadow-sm hover:scale-105 transition ${isSelectionDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-brandOcean border-brandOcean'
                      : 'bg-white border-gray-300'
                  }`}
                >
                  {isSelected && (
                    <span className="text-white text-sm font-bold">✓</span>
                  )}
                </div>
              </button>
            )}
          </div>
        </div>

        <Card
          radius="none"
          shadow="none"
          className="  bg-transparent transition-all duration-300 ease-in-out"
        >
          <CardHeader className="pb-0 pt-2 px-4 flex-col items-start"></CardHeader>
          <CardBody className="overflow-visible -mt-5 sm:-mt-3 pb-3">
            <div className="flex flex-col-reverse  sm:flex-row justify-between items-baseline">
              <div className="flex flex-initial mb-1 items-center ">
                <div>
                  <SlLocationPin
                    size={12}
                    className="mr-1 text-secondary -mb-1"
                  />
                </div>

                <small className="text-caption line-clamp-1 mt-2">
                  {locationCity}, {locationState}
                </small>
              </div>

              {data?.isPackage && (
                <div className="">
                  <small className=" bg-purple-100 text-gray-600 px-2 py-1 rounded-md text-[11px] font-medium">
                    Package Includes
                  </small>
                </div>
              )}
            </div>
            <Link
              to={`${routes.serviceDetails}/${data.displayId || data.serviceId}/${data.slug}`}
              onClick={(e) => {
                e.stopPropagation();
                if (sponsorshipId)
                  trackSponsorshipEvent(sponsorshipId, 'click');
              }}
            >
              <p className="text-[13px] font-semibold text-gray-700 whitespace-break-spaces line-clamp-2 md:line-clamp-1 mt-1 hover:underline">
                {data?.serviceTitle || 'No title'}
              </p>
            </Link>

            <div>
              <div className="flex items-center justify-between mt-2 sm:-mt-1 py-1 pl-2 xl:pl-0">
                {/* Rating */}
                <div className="flex flex-initial gap-3">
                  {mode === 'default' && publicFavoriteCount > 0 && (
                    <div className="flex flex-initial justify-center items-center ">
                      <FaHeart
                        className="text-pink-500 hover:text-pink-400 mr-1 "
                        size={14}
                      />
                      <small className="-mb-[2px] font-medium ">
                        {formatCompactNumber(publicFavoriteCount)}
                      </small>
                    </div>
                  )}

                  {displayReviewCount > 0 ? (
                    <div className="flex flex-initial justify-center items-center ">
                      <FaStar className="text-yellow-500  mr-1 " size={14} />
                      <small className="-mb-1 font-medium ">
                        {Number(displayAverageRating ?? 0).toFixed(1)}
                        <span className="text-caption ">
                          {' '}
                          ({displayReviewCount})
                        </span>
                      </small>
                    </div>
                  ) : (
                    <div className="flex flex-initial justify-center items-center ">
                      <small className="-mb-1 bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-[11px] font-medium">
                        {t('service_card.new_provider')}
                      </small>
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-2">
                  {isPackageBooking && (
                    <p className="text-body-bold text-secondary items-end">
                      Start from
                    </p>
                  )}

                  <div className="flex items-baseline gap-1">
                    {isPackageBooking && (
                      <p className="text-title2 text-gray-700">
                        {formatCurrency(getServiceMinTierPrice(data)) || '-'}
                      </p>
                    )}

                    {isQuoteBooking && (
                      <span className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                        Request a Quote
                      </span>
                    )}

                    {!isQuoteBooking && !isPackageBooking && (
                      <p className="text-title2 text-gray-700">
                        {formatCurrency(data?.price) || '-'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-end flex-col sm:flex-row justify-between mt-3 ">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const businessPath = getBusinessProfilePath(data);
                    if (businessPath) {
                      navigate(businessPath);
                      return;
                    }
                    navigate(
                      `/error?origin=app&code=${ERROR_CODE.RESOURCE_NO_LONGER_AVAILABLE}`
                    );
                  }}
                  className="flex flex-initial cursor-pointer w-full sm:w-auto"
                >
                  <div className="flex items-baseline justify-start flex-col ml-1.5 xl:ml-0">
                    <p className="text-body text-gray-700 text-start whitespace-break-spaces hover:underline line-clamp-1">
                      {data?.businessName || data?.business?.name || 'N/A'}
                    </p>

                    {mode === 'default' && (
                      <p className="item-start text-body text-gray-500">
                        {data?.location?.[0]?.state || '-'}
                      </p>
                    )}
                  </div>
                </button>

                {mode === 'favoriteDashboard' ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <CommonConfirmModal
                      buttonLabel="Remove"
                      buttonColor="danger"
                      buttonVariant="flat"
                      title="Remove favorite service?"
                      description="Do you want to remove this service from your favorite list?"
                      iconType="warning"
                      confirmBtnText="Yes, Remove"
                      cancelBtnText="No"
                      confirmBtnColor="danger"
                      isLoading={isRemoveLoading}
                      isDisabled={isRemoveLoading}
                      onConfirm={() =>
                        onRemoveFavorite?.(String(data?.serviceId || ''))
                      }
                    />
                  </div>
                ) : (
                  <div onClick={(e) => e.stopPropagation()}>
                    <CustomButton
                      label={bookingCtaLabel}
                      variant="flat"
                      color="primary"
                      className=" w-full sm:w-auto mt-3 sm:mt-0"
                      size="sm"
                      startContent={<FaRegBookmark />}
                      onPress={() =>
                        handleBookNowClick(data?.displayId || data?.serviceId)
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            <MessageModal
              isOpen={isModalOpen}
              title="Login Required to Continue"
              onClose={() => setIsModalOpen(false)}
            >
              <div>
                {/* Modal content goes here */}
                <p className="text-body1 text-gray-800 text-center px-10">
                  Please Log in to your account. Logging in helps us manage your
                  account securely.
                </p>
              </div>
            </MessageModal>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default ServiceCard;
